import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { installBlockers } from "../pwa/installBlockers";

import {
  appEnvironment,
  buildInfo,
  formatBuiltAt,
  shortCommit,
} from "../config/build";
import {
  defaultInstallGuide,
  installGuides,
  type InstallGuideId,
} from "../pwa/install";
import {
  useConnection,
  useInstall,
  usePwa,
  useServiceWorkerUpdate,
} from "../pwa/pwaServices";
import {
  currentPlatform,
  localPushSubscription,
  pushPermission,
  pushStatusLabel,
} from "../web-push";

const environmentLabels = {
  local: "Локальный стенд",
  testing: "Тестовый контур",
  staging: "Стенд (staging)",
  production: "Рабочий контур",
} as const;

/**
 * Раздел «О приложении».
 *
 * Здесь всё, что нужно знать о самом приложении, а не о данных в нём: какая
 * версия работает, установлено ли оно, есть ли обновление и куда приходят
 * уведомления. Сведений, которые нельзя показывать, здесь нет: только версия,
 * коммит и время сборки.
 */
export function AboutPage() {
  const { updater, installer } = usePwa();
  const update = useServiceWorkerUpdate();
  const install = useInstall();
  const connection = useConnection();
  const platform = currentPlatform();
  const installed = install.standalone || install.installedNow;
  const environment = appEnvironment();
  const [guideId, setGuideId] = useState<InstallGuideId>(() =>
    defaultInstallGuide(platform, navigator.userAgent),
  );
  const [installNotice, setInstallNotice] = useState<string | null>(null);
  const [showBlockers, setShowBlockers] = useState(false);
  // Safari на iPhone и iPad не показывает окно установки ни при каких условиях:
  // кнопка там ничего бы не сделала, поэтому вместо неё сразу даём шаги.
  const manualIos = platform === "ios" && !install.canPrompt;
  const blockers = installBlockers({
    secureContext: window.isSecureContext,
    serviceWorkerSupported: "serviceWorker" in navigator,
    workerRegistered: update.registered,
    platform,
    userAgent: navigator.userAgent,
  });
  const guide =
    installGuides.find((item) => item.id === guideId) ?? installGuides[0];
  const push = useQuery({
    queryKey: ["about", "push"],
    queryFn: async () => ({
      permission: pushPermission(),
      subscribed: (await localPushSubscription()) !== null,
    }),
    retry: false,
  });

  return (
    <main className="content-page about-page" aria-labelledby="about-title">
      <header className="page-header">
        <div>
          <p className="eyebrow">Приложение</p>
          <h1 id="about-title">О приложении</h1>
          <p>
            Версия, установка и обновления ERP. Здесь нет паролей, токенов и
            другой закрытой информации.
          </p>
        </div>
      </header>

      <div className="health-grid">
        <section className="health-card" aria-labelledby="about-build-title">
          <h2 id="about-build-title">Сборка</h2>
          <dl>
            <div>
              <dt>Версия</dt>
              <dd>{buildInfo.version}</dd>
            </div>
            <div>
              <dt>Коммит</dt>
              <dd>
                <code title={buildInfo.commit}>
                  {shortCommit(buildInfo.commit)}
                </code>
              </dd>
            </div>
            <div>
              <dt>Собрано</dt>
              <dd>
                <time dateTime={buildInfo.builtAt}>
                  {formatBuiltAt(buildInfo.builtAt)}
                </time>
              </dd>
            </div>
            {environment === null ? null : (
              <div>
                <dt>Контур</dt>
                <dd>{environmentLabels[environment]}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="health-card" aria-labelledby="about-update-title">
          <h2 id="about-update-title">Обновления</h2>
          <p
            className={`status-badge status-badge--${update.status === "available" || update.status === "reload-recommended" ? "pending" : "ready"}`}
          >
            {!update.supported
              ? "Недоступны"
              : update.status === "available"
                ? "Есть новая версия"
                : update.status === "reload-recommended"
                  ? "Нужна перезагрузка"
                  : "Актуальная версия"}
          </p>
          <p className="dw-secondary">
            {!update.supported
              ? "Для обновлений и работы без адресной строки нужен защищённый адрес (HTTPS) и современный браузер."
              : !update.registered
                ? "Фоновая служба ещё не запущена. Она включается в собранном приложении."
                : update.status === "available"
                  ? "Новая версия загружена. Применить её можно в сообщении внизу экрана — оно спросит, если есть несохранённый ввод."
                  : update.lastCheckedAt === null
                    ? "Приложение само проверяет обновления при запуске и раз в час."
                    : "Проверено только что: установлена актуальная версия."}
          </p>
          {update.supported && update.registered ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={update.checking}
              onClick={() => void updater.check()}
            >
              {update.checking ? "Проверяем…" : "Проверить обновление"}
            </button>
          ) : null}
        </section>

        <section className="health-card" aria-labelledby="about-network-title">
          <h2 id="about-network-title">Соединение</h2>
          <p
            className={`status-badge status-badge--${connection.status === "online" ? "ready" : "pending"}`}
          >
            {connection.status === "online"
              ? "Онлайн"
              : connection.status === "reconnecting"
                ? "Восстанавливаем"
                : "Нет соединения"}
          </p>
          <p className="dw-secondary">
            ERP не сохраняет изменения без связи: без сети можно смотреть уже
            загруженное, а после её возвращения данные перечитываются с сервера.
          </p>
        </section>

        <section className="health-card" aria-labelledby="about-push-title">
          <h2 id="about-push-title">Уведомления</h2>
          <p className="dw-secondary">
            {push.data === undefined
              ? "Проверяем…"
              : pushStatusLabel(
                  push.data.permission,
                  push.data.subscribed,
                  installed,
                  platform,
                )}
          </p>
          <Link
            className="button button--secondary button--compact"
            to="/notifications"
          >
            Настроить уведомления
          </Link>
        </section>
      </div>

      <section
        id="install"
        className="health-card about-install"
        aria-labelledby="about-install-title"
      >
        <h2 id="about-install-title">Установка на устройство</h2>
        <p
          className={`status-badge status-badge--${installed ? "ready" : "pending"}`}
        >
          {installed ? "Установлено" : "Не установлено"}
        </p>
        {installed ? (
          <p className="dw-secondary">
            ERP запущена как приложение: у неё своё окно и иконка на устройстве.
          </p>
        ) : (
          <>
            <p className="dw-secondary">
              Установленное приложение открывается с иконки в отдельном окне, а
              на iPhone и iPad только так работают уведомления.
            </p>
            {manualIos ? (
              <p className="operation-notice about-install__manual" role="note">
                <strong>На iPhone и iPad установка выполняется вручную.</strong>{" "}
                Safari не показывает окно установки — выполните шаги ниже, и
                ERP появится на экране «Домой».
              </p>
            ) : (
            <button
              className="button button--primary button--compact"
              type="button"
              onClick={() => {
                setInstallNotice(null);
                if (!install.canPrompt) {
                  // Установить сейчас нельзя — объясняем почему и что делать.
                  setShowBlockers(true);
                  return;
                }
                void installer.prompt().then((outcome) => {
                  if (outcome === "dismissed") {
                    setInstallNotice(
                      "Установка отменена. Её можно начать снова в любой момент.",
                    );
                  }
                });
              }}
            >
              Установить приложение
            </button>
            )}
            {install.canPrompt || manualIos || !showBlockers ? null : (
              <div
                className="operation-notice operation-notice--error"
                role="alert"
              >
                <strong>Установить приложение сейчас не получится.</strong>
                <ul className="about-install__blockers">
                  {blockers.map((blocker) => (
                    <li key={blocker.problem}>
                      {blocker.problem} <b>Что делать:</b> {blocker.fix}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {installNotice === null ? null : (
              <p className="operation-notice" role="status">
                {installNotice}
              </p>
            )}
            <div
              className="notification-help__platforms"
              role="group"
              aria-label="Устройство и браузер"
            >
              {installGuides.map((item) => (
                <button
                  key={item.id}
                  className="notification-help__platform"
                  type="button"
                  aria-pressed={guideId === item.id}
                  onClick={() => setGuideId(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {guide === undefined ? null : (
              <div className="about-install__guide">
                <ol>
                  {guide.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                {guide.fallback === undefined ? null : (
                  <p className="dw-secondary">{guide.fallback}</p>
                )}
                {guide.note === undefined ? null : (
                  <p className="dw-secondary">{guide.note}</p>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
